import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
});

// Decode JWT exp to detect near-expiry tokens (no signature verification — client-side hint only).
const jwtExpSeconds = (token) => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return typeof payload.exp === "number" ? payload.exp : null;
  } catch {
    return null;
  }
};

const REFRESH_THRESHOLD_SEC = 6 * 60 * 60; // refresh when <6h to expiry
let refreshing = null;

const tryRefresh = async () => {
  if (refreshing) return refreshing;
  const token = localStorage.getItem("jastip_token");
  if (!token) return null;
  refreshing = axios
    .post(`${API}/auth/refresh`, null, { headers: { Authorization: `Bearer ${token}` } })
    .then((r) => {
      if (r.data?.token) {
        localStorage.setItem("jastip_token", r.data.token);
        if (r.data.user) localStorage.setItem("jastip_user", JSON.stringify(r.data.user));
      }
      return r.data?.token || null;
    })
    .catch(() => null)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
};

api.interceptors.request.use(async (config) => {
  let token = localStorage.getItem("jastip_token");
  // Sliding-window refresh: if token is close to expiry, attempt refresh BEFORE the request.
  if (token && !config.url?.includes("/auth/refresh")) {
    const exp = jwtExpSeconds(token);
    const nowSec = Math.floor(Date.now() / 1000);
    if (exp && exp - nowSec < REFRESH_THRESHOLD_SEC && exp - nowSec > 0) {
      const refreshed = await tryRefresh();
      if (refreshed) token = refreshed;
    }
  }
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      const path = window.location.pathname;
      if (path !== "/auth" && path !== "/" && path !== "/admin/login") {
        localStorage.removeItem("jastip_token");
        localStorage.removeItem("jastip_user");
      }
    }
    return Promise.reject(err);
  }
);

export const fileUrl = (path) => (path ? `${API}/files/${path}` : null);

export const formatIDR = (n) => {
  if (n === null || n === undefined) return "Rp 0";
  return new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(n);
};

export const formatDate = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

export const formatDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

// Map FastAPI error detail (string | array | object) to a flat string for display.
export const formatApiError = (err) => {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Terjadi kesalahan. Coba lagi.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" · ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};
